import { BookingTaskResults } from '@/app/(UnmoderatedStudy)/unmod/data/BookingTaskResults.query';

import type { CreateBookingTaskResults } from '@/app/(UnmoderatedStudy)/unmod/data/CreateBookingTaskResults.mutation';
import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

type TaskResultsQuery = ResultOf<typeof BookingTaskResults>;
type TaskResultNode = TaskResultsQuery['participantBookingTaskResults']['nodes'][number];

export const taskResultMutationUpdates = {
  /**
   * Joins created rows to the `participantBookingTaskResults` list. graphcache
   * normalises the returned rows on its own but cannot know which list field
   * they belong to, so without this the list keeps the length it was fetched
   * with until it is refetched.
   *
   * The mutation returns every row for the booking, existing ones included — a
   * backend convention the schema does not enforce. Both the dedupe and the
   * refusal to populate a list nothing has fetched hang off that.
   */
  createBookingTaskResults(
    result: ResultOf<typeof CreateBookingTaskResults>,
    args: VariablesOf<typeof CreateBookingTaskResults>,
    cache: Cache,
  ) {
    const created = result.createBookingTaskResults;
    if (!created?.length) return;

    cache.updateQuery<TaskResultsQuery, VariablesOf<typeof BookingTaskResults>>(
      { query: BookingTaskResults, variables: { _booking_id: args.input._booking_id } },
      data => {
        // Nothing has fetched the list for this booking yet. Appending to a list
        // already held stays correct whatever subset the mutation returns; writing
        // one from scratch stays correct only while it returns all of them.
        if (!data) return null;

        const nodes = data.participantBookingTaskResults.nodes;
        const known = new Set(nodes.map(node => node._id));
        // Rows the list already holds arrive again on every call.
        const added = created
          .filter(row => !known.has(row._id))
          // Written through the list query's selection, so the node carries the
          // field's own type name rather than the mutation's. The row's identity
          // is the `_id` either way — see taskResultGlobalIds.
          .map(row => ({ ...row, __typename: 'ParticipantBookingTaskResult' as const }) as TaskResultNode);

        if (!added.length) return data;

        return {
          ...data,
          participantBookingTaskResults: {
            ...data.participantBookingTaskResults,
            nodes: [...nodes, ...added],
          },
        };
      },
    );
  },
};
